// ============================================================
// targetPanelModel - the Target panel's writes, as pure functions
// ============================================================
// What stepping through the roster lands on, what frenzy strips (and the
// flash that says so), and the small target rewrites the
// panel hands to `updateTab`. Every one returns a NEW target.

import type { Target } from '../types';
import type { StackPolicy } from '../engine/stackPolicy';
import { computeTraitCleanup } from '../engine/conditionEligibility';
import { flashOf, type CleanedTarget } from '../engine/traitCleanup';
import { mergeCondition } from '../engine/conditions';
import deepClone from '../utils/deepClone';

/** Frenzy switched on: the conditions it is immune to (Stunned, Fatigued) go. */
export function frenzyOn(target: Target): CleanedTarget {
  const next = deepClone(target);
  next.frenzied = true;
  const result = computeTraitCleanup(next.conditions, 'frenzied', true);
  next.conditions = result.cleaned;
  return { target: next, flash: flashOf(result) };
}

/** The conditions with `code` merged in by the stacking policy. */
export function withCondition(conditions: string[], code: string, policy: StackPolicy): string[] {
  const next = [...conditions];
  mergeCondition(next, code, policy);
  return next;
}

/** The target with its standing reaction set - ABSENT, never undefined, when cleared (Firestore). */
export function withStandingReaction(target: Target, value: Target['standingReaction'] | null): Target {
  const next = { ...target };
  if (value) next.standingReaction = value;
  else delete next.standingReaction;
  return next;
}

/** The target's live health set: a horde's magnitude, anyone else's current wounds. */
export function withHealth(target: Target, value: number): Target {
  if (target.horde?.isHorde) return { ...target, horde: { ...target.horde, magnitude: value } };
  return { ...target, wounds: { ...target.wounds, current: value } };
}
